<template>
  <!-- Discover View -->
  <MainLayout
    bg-color="white"
    no-content-padding
    aditional-classes="relative"
  >
    <template #toolbar>
      <div class="flex flex-col w-full">
        <!-- Top Header -->
        <div class="relative flex items-center justify-between p-3 border-b border-border-normal">
          <div class="flex items-center gap-3">
            <img
              src="../../assets/icons/briefs.png"
              class="w-8 h-8"
            >
            <BaseText
              type="label"
              size="sm"
              class="text-text-muted"
            >
              Briefs
            </BaseText>
          </div>

          <!-- Search Bar -->
          <div
            class="absolute flex top-1/2 transform -translate-y-1/2"
            style="left: 308px; right: 308px; z-index: 40001"
          >
            <div
              class="relative flex-grow mx-auto min-w-0 h-8"
              style="max-width: 600px"
            >
              <BaseInputV2
                icon
                placeholder="Search for briefs..."
                class="w-full"
                :value="searchText"
                @input="handleBriefSearch"
              >
                <img
                  slot="icon"
                  src="../../assets/icons/search.svg"
                  class="h-5 w-5"
                >
              </BaseInputV2>
            </div>
          </div>

          <div class="flex items-center">
            <BaseButton
              class="mr-3"
              @click="showTutorialModal = true"
            >
              <div class="flex items-center">
                <img
                  src="../../assets/icons/tutorial.svg"
                  alt=""
                  class="mr-1.5"
                >
                <BaseText
                  type="label"
                  size="sm"
                  class="text-text-muted"
                >
                  Tutorial
                </BaseText>
              </div>
            </BaseButton>
            <BaseButton
              primary
              icon-only
              @click="showCreateBriefModal = true"
            >
              <div class="flex items-center gap-1 pr-1.5">
                <PlusIcon />
                <BaseText
                  type="label"
                  size="sm"
                  class="text-white"
                >
                  New
                </BaseText>
              </div>
            </BaseButton>
          </div>
        </div>
        <!-- Subview Tabs -->
        <div class="flex gap-2 w-full px-3 py-2">
          <div
            class="library-view-tab"
            :class="{'active': viewToggleValue === 'Briefs'}"
            @click="(viewToggleValue = 'Briefs'), (transitioning = true), transitionTimeout()"
          >
            <BriefsTabIcon class="w-5 h-5" />
            Briefs
          </div>
          <div
            class="library-view-tab"
            :class="{'active': viewToggleValue === 'Brand Profiles'}"
            @click="(viewToggleValue = 'Brand Profiles'), (transitioning = true), transitionTimeout()"
          >
            <BrandProfilesIcon class="w-5 h-5" />
            Brand Profiles
          </div>
        </div>
      </div>
    </template>

    <template #content>
      <div class="flex flex-col bg-white">
        <!-- Filter and Search -->
        <transition
          name="fade"
          enter-active-class="transition-opacity duration-300"
          leave-active-class="transition-opacity duration-300"
          enter-class="opacity-0"
          enter-to-class="opacity-100"
          leave-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div
            v-if="viewToggleValue === 'Briefs' && !transitioning"
            class="flex items-center justify-between border-b border-border-normal py-3 px-5"
          >
            <div class="flex items-center gap-1">
              <!-- Brand Profile Filter -->
              <div class="w-56 min-w-0 relative">
                <!-- Actual Dropdown -->
                <BaseSingleDropdown
                  v-if="brandProfiles && brandProfiles.length > 0 && !brandProfilesLoading"
                  :disabled="loading"
                  class="w-full"
                  placeholder="Brand Profile"
                  :options="[
                    { label: 'Show All Brands', value: null },
                    ...brandProfiles.map(bp => ({ label: bp.name, value: bp.id, image: bp.image }))
                  ]"
                  option-key="value"
                  search-key="label"
                  enable-search
                  :max-width="'100%'"
                  min-height="34px"
                  use-parent-width
                  :selected-obj="selectedBrandProfileFilter || { label: 'All Brand Profiles', value: null }"
                  @change="handleBrandProfileFilterChange"
                >
                  <template #icon>
                    <img
                      v-if="selectedBrandProfileFilter && selectedBrandProfileFilter.image"
                      :src="selectedBrandProfileFilter.image"
                      class="w-4 h-4 rounded-full mr-1 flex-shrink-0"
                    >
                    <div
                      v-else-if="selectedBrandProfileFilter && selectedBrandProfileFilter.value !== null"
                      class="w-4 h-4 rounded-full bg-background-normal mr-1 flex-shrink-0 flex items-center justify-center"
                    >
                      <BaseText type="label" size="xs" class="text-text-muted">
                        {{ selectedBrandProfileFilter.label.charAt(0).toUpperCase() }}
                      </BaseText>
                    </div>
                    <svg
                      v-else
                      width="16"
                      height="16"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3.95841 16.875V4.79167C3.95841 3.87119 4.70461 3.125 5.62508 3.125H14.3751C15.2956 3.125 16.0417 3.87119 16.0417 4.79167V16.875M3.95841 16.875H16.0417M3.95841 16.875H2.29175M16.0417 16.875H17.7084M7.29175 6.45833H8.54175M11.4584 6.45833H12.7084M7.29175 9.79167H8.54175M11.4584 9.79167H12.7084M7.29175 13.125H8.54175M11.4584 13.125H12.7084"
                        stroke="#5E6678"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </template>

                  <template #option-icon="{ option }">
                    <img
                      v-if="option.image"
                      :src="option.image"
                      class="w-6 h-6 rounded-full mr-2 flex-shrink-0"
                    >
                    <div
                      v-else-if="option.value !== null"
                      class="w-6 h-6 rounded-full bg-background-normal mr-2 flex-shrink-0 flex items-center justify-center"
                    >
                      <BaseText type="label" size="xs" class="text-text-muted">
                        {{ option.label.charAt(0).toUpperCase() }}
                      </BaseText>
                    </div>
                    <img
                      v-else
                      src="../../assets/icons/brand-profile.svg"
                      alt=""
                      class="w-6 h-6 rounded-full bg-background-normal p-0.5 mr-2 flex-shrink-0"
                    >
                  </template>
                </BaseSingleDropdown>
              </div>

              <!-- Status Filter -->
              <div class="ml-2 w-52 min-w-0 relative">
                <!-- Actual Status Dropdown -->
                <BaseSingleDropdown
                  v-if="!brandProfilesLoading"
                  :disabled="loading"
                  class="w-full"
                  placeholder="Status"
                  no-text
                  :max-width="'100%'"
                  min-height="34px"
                  :options="[
                    { label: 'All Statuses', value: null },
                    { label: 'No Status', value: 'No Status' },
                    { label: 'To Do', value: 'To Do' },
                    { label: 'In Progress', value: 'In Progress' },
                    { label: 'Needs Review', value: 'Needs Review' },
                    { label: 'Completed', value: 'Completed' }
                  ]"
                  option-key="value"
                  search-key="label"
                  use-parent-width
                  :selected-obj="selectedStatusFilter || { label: 'All Statuses', value: null }"
                  @change="handleStatusFilterChange"
                >
                  <!-- Icon slot for the selected status (shown in trigger) -->
                  <template #icon>
                    <BaseBadge
                      v-if="selectedStatusFilter && selectedStatusFilter.value !== null"
                      :blue="selectedStatusFilter.value === 'In Progress'"
                      :green="selectedStatusFilter.value === 'Completed'"
                      :yellow="selectedStatusFilter.value === 'Needs Review'"
                      :gray="selectedStatusFilter.value === 'To Do' || selectedStatusFilter.value === 'No Status'"
                      dot
                      class="mr-1"
                    >
                      <BaseText type="label" size="xs" class="mr-1">
                        {{ selectedStatusFilter.label }}
                      </BaseText>
                    </BaseBadge>
                    <BaseText
                      v-else-if="selectedStatusFilter && selectedStatusFilter.label === 'All Statuses'"
                      type="label"
                      size="sm"
                      class="text-text-muted mr-1"
                    >
                      {{ selectedStatusFilter.label }}
                    </BaseText>
                  </template>

                  <!-- Template for dropdown options -->
                  <template #option-icon="{ option }">
                    <BaseBadge
                      v-if="option.value !== null"
                      :blue="option.value === 'In Progress'"
                      :green="option.value === 'Completed'"
                      :yellow="option.value === 'Needs Review'"
                      :gray="option.value === 'To Do' || option.value === 'No Status'"
                      dot
                      class="mr-auto"
                    >
                      <BaseText type="label" size="xs">
                        {{ option.label }}
                      </BaseText>
                    </BaseBadge>
                    <BaseText
                      v-else
                      type="label"
                      size="sm"
                      class="text-text-muted mr-1.5"
                    >
                      {{ option.label }}
                    </BaseText>
                  </template>
                </BaseSingleDropdown>
              </div>
            </div>

            <!-- Sort Dropdown -->
            <div class="text-text-muted flex justify-end w-96">
              <BaseSelect
                :items="[
                  { name: 'Newest', rightText: '1 → 9', value: 'desc' },
                  { name: 'Oldest', rightText: '9 → 1', value: 'asc' },
                ]"
                :selected.sync="sortOrder"
                popper-center
                item-value="value"
                placement="null"
                @select="handleSort"
              >
                <div
                  slot="reference"
                  class="flex gap-2"
                >
                  <img src="../../assets/icons/sort.svg">
                  <BaseText
                    type="body"
                    size="sm"
                  >
                    Sort By
                  </BaseText>

                  <img src="../../assets/icons/chevron-down-small.svg">
                </div>

                <template #item="{ item }">
                  <div class="flex justify-between w-full">
                    <BaseText
                      type="label"
                      size="sm"
                    >
                      {{ item.name }}
                    </BaseText>

                    <div class="text-neutral-500">
                      <BaseText
                        type="body"
                        size="sm"
                      >
                        {{ item.rightText }}
                      </BaseText>
                    </div>
                  </div>
                </template>
              </BaseSelect>
            </div>
          </div>
        </transition>
        <!-- Content Section -->
        <transition
          name="fade"
          enter-active-class="transition-opacity duration-300"
          leave-active-class="transition-opacity duration-300"
          enter-class="opacity-0"
          enter-to-class="opacity-100"
          leave-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div
            v-if="viewToggleValue === 'Briefs' && !transitioning"
            class="-my-2 overflow-x-auto min-h-screen"
          >
            <div
              class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8"
            >
              <!-- Briefs Table -->
              <transition
                name="fade"
                enter-active-class="transition-opacity duration-500"
                leave-active-class="transition-opacity duration-500"
                enter-class="opacity-0"
                enter-to-class="opacity-100"
                leave-class="opacity-100"
                leave-to-class="opacity-0"
              >
                <div v-if="getBriefs.length && !loading">
                  <div>
                    <!-- HEADERS -->
                    <div class="flex border-b border-neutral-50 py-3">
                      <!-- Bulk Checkbox -->
                      <div class="flex items-center justify-end px-1.5">
                        <!-- Checkbox -->
                        <input
                          type="checkbox"
                          class="h-4 w-4 ml-2 mr-3 rounded border-border-normal text-primary-green-100 focus:ring-0"
                          :checked="selectedBriefs.length === getBriefs.length"
                          @click.stop="selectAllBriefs($event)"
                        >
                      </div>

                      <!-- The PR is to displace the ... options -->
                      <div
                        class="flex items-center justify-between w-full pr-5"
                      >
                        <!-- Name -->
                        <div>
                          <BaseText
                            type="body"
                            size="sm"
                            class="text-neutral-400"
                          >
                            Name
                          </BaseText>
                        </div>

                        <!-- Brand -->
                        <div>
                          <BaseText
                            type="body"
                            size="sm"
                            class="text-neutral-400"
                          >
                            Brand
                          </BaseText>
                        </div>

                        <!-- Folder -->
                        <div>
                          <BaseText
                            type="body"
                            size="sm"
                            class="text-neutral-400"
                          >
                            Folder
                          </BaseText>
                        </div>

                        <!-- Status -->
                        <div>
                          <BaseText
                            type="body"
                            size="sm"
                            class="text-neutral-400"
                          >
                            Status
                          </BaseText>
                        </div>

                        <!-- Due Date -->
                        <div>
                          <BaseText
                            type="body"
                            size="sm"
                            class="text-neutral-400"
                          >
                            Due Date
                          </BaseText>
                        </div>

                        <!-- Blank for settings -->
                        <div class="col-span-1" />
                      </div>
                    </div>
                    <img
                      v-if="!filteredBriefs.length && !showAllBriefs"
                      src="../../assets/images/empty-with-filters.gif"
                      alt=""
                      class="mx-auto mt-10 mb-5 shadow"
                    >
                    <BaseText
                      v-if="!filteredBriefs.length && !showAllBriefs"
                      type="body"
                      size="sm"
                      class="text-center mx-auto mb-10 text-gray-700"
                    >
                      <span class="text-lg font-bold my-5 block text-black">
                        Too many filters
                      </span>
                      No briefs found that meet your current filters
                    </BaseText>
                    <!-- <BaseButton
                      v-if="!filteredBriefs.length && !showAllBriefs"
                      class="mx-auto mt-10 mb-5 shadow"
                      cta
                      @click="
                        () => {

                        }
                      "
                    >
                      Clear filters
                    </BaseButton> -->
                    <!-- ROWS -->
                    <transition-group
                      v-if="getBriefs.length"
                      name="fade"
                      enter-active-class="transition-opacity duration-200"
                      leave-active-class="transition-opacity duration-200"
                      enter-class="opacity-0"
                      enter-to-class="opacity-100"
                      leave-class="opacity-100"
                      leave-to-class="opacity-0"
                    >
                      <div
                        v-for="brief of briefs"
                        :key="brief.id"
                        class="flex items-center py-5 border-b border-neutral-50 cursor-pointer hover:bg-background-disabled transition-opacity duration-300"
                        :class="{
                          'bg-background-disabled': selectedBriefs.includes(
                            brief.id
                          ),
                          hidden:
                            !filteredBriefs.includes(brief) && !showAllBriefs,
                        }"
                        @click="goToBrief(brief.id)"
                      >
                        <!-- Checkbox -->
                        <div class="flex items-center justify-end px-1.5">
                          <!-- Checkbox -->
                          <input
                            type="checkbox"
                            class="h-4 w-4 ml-2 mr-3 rounded border-border-normal text-primary-green-100 focus:ring-0 cursor-pointer"
                            :checked="selectedBriefs.includes(brief.id)"
                            @click.stop="toggleSelectedBrief(brief.id)"
                          >
                        </div>

                        <div class="grid grid-cols-5 w-full">
                          <!-- Name -->
                          <div>
                            <BaseTooltip placement="top-start">
                              <BaseText
                                slot="reference"
                                type="label"
                                size="sm"
                                class="text-neutral-700 font-medium w-36 truncate"
                              >
                                {{ brief.name }}
                              </BaseText>

                              <!-- Tooltip content -->
                              <div
                                slot="content"
                                class="px-1.5"
                              >
                                {{ brief.name }}
                              </div>
                            </BaseTooltip>
                          </div>
                          <!-- Brand -->
                          <div class="flex items-center gap-2">
                            <img
                              v-if="brief.brandProfileId"
                              class="w-6 h-6 rounded-full"
                              :src="
                                brandProfiles.find(
                                  (bp) => bp.id === brief.brandProfileId
                                )?.image
                              "
                            >

                            <BaseText
                              v-if="brief.brandProfileId"
                              type="body"
                              size="sm"
                              class="font-normal text-neutral-700 w-32 truncate"
                            >
                              {{
                                brandProfiles.find(
                                  (bp) => bp.id === brief.brandProfileId
                                )?.name
                              }}
                            </BaseText>
                            <BaseText
                              v-else
                              type="body"
                              size="sm"
                              class="text-neutral-400 font-normal"
                            >
                              {{ "No Brand Selected" }}
                            </BaseText>
                          </div>

                          <!-- Folder -->
                          <div class="flex items-center gap-2">
                            <!--  -->
                            <img src="../../assets/icons/folder.svg">

                            <BaseText
                              type="body"
                              size="sm"
                              class="text-neutral-700 font-normal w-36 truncate"
                            >
                              {{ getBriefFolder(brief.id).name }}
                            </BaseText>
                          </div>
                          <!-- Status -->
                          <div class="flex items-center">
                            <!--  -->
                            <BaseBadge
                              slot="reference"
                              :blue="brief.status === 'In Progress'"
                              :green="brief.status === 'Completed'"
                              :yellow="brief.status === 'Needs Review'"
                              :gray="brief.status === 'To Do' || !brief.status"
                              dot
                            >
                              <BaseText
                                type="label"
                                size="xs"
                              >
                                {{ brief.status || "No Status" }}
                              </BaseText>
                            </BaseBadge>
                          </div>
                          <!-- Due Date -->
                          <div>
                            <BaseText
                              type="body"
                              size="sm"
                              class="text-neutral-400 font-normal"
                            >
                              {{
                                brief.dueDate
                                  ? formatDate(brief.dueDate)
                                  : "No Date Set"
                              }}
                            </BaseText>
                          </div>
                        </div>
                        <div class="pr-4">
                          <BaseSelect
                            :items="[
                              {
                                name: 'Share',
                                action: shareBrief,
                                divider: true,
                              },
                              { name: 'Duplicate', action: duplicateBrief },
                              {
                                name: 'Archive',
                                action: archiveBrief,
                                divider: true,
                              },
                              { name: 'Delete', action: deleteBrief },
                            ]"
                            text-trigger
                            popper-center
                            no-padding
                            compact
                            item-value="name"
                            @click.native.stop
                            @select="() => {}"
                          >
                            <!-- Three Dots -->
                            <img
                              slot="reference"
                              class="hover:bg-white border border-transparent hover:border-border-normal rounded-md transition"
                              src="../../assets/icons/dots.svg"
                            >

                            <!-- Dropdown Items -->
                            <template #item="{ item }">
                              <div
                                class="flex max-w-max"
                                style="width: max-content"
                                @click="item.action(brief.id)"
                              >
                                <img
                                  v-if="item.name === 'Delete'"
                                  class="pr-2"
                                  src="../../assets/icons/trash.svg"
                                >
                                <img
                                  v-if="item.name === 'Archive'"
                                  class="pr-2"
                                  src="../../assets/icons/archive.svg"
                                >
                                <img
                                  v-if="item.name === 'Duplicate'"
                                  class="pr-2"
                                  src="../../assets/icons/copy.svg"
                                >
                                <img
                                  v-if="item.name === 'Share'"
                                  class="pr-2"
                                  src="../../assets/icons/share.svg"
                                >
                                <BaseText
                                  type="body"
                                  size="sm"
                                >
                                  {{ item.name }}
                                </BaseText>
                              </div>
                            </template>
                          </BaseSelect>
                        </div>
                      </div>
                    </transition-group>
                  </div>
                </div>
                <div
                  v-else-if="loading"
                  class="flex items-center justify-center h-full"
                >
                  <BaseLoadingLogo class="mt-24" />
                </div>
              </transition>
              <!-- Pagination loading -->
              <div
                v-if="!loading"
                class="flex w-full items-center justify-center my-4"
              >
                <BaseButton
                  v-if="getLastBriefDoc"
                  :disabled="loadingMore"
                  :loading="loadingMore"
                  outlined
                  @click="getMoreBriefs"
                >
                  Load more
                </BaseButton>
                <BaseText
                  v-else-if="getBriefs.length > 0"
                  class="text-center"
                  type="label"
                  size="md"
                >
                  All Briefs Loaded
                </BaseText>
              </div>
              <!-- Check if briefs has initially loaded -->
              <div
                v-if="!getBriefs.length && !getInitialLoad"
                class="w-full h-96 flex items-center justify-center"
              >
                <BaseLoadingLogo />
              </div>
              <!-- No results state -->
              <div
                v-if="searchText.length > 0 && getBriefs.length == 0 && !loading"
                class="mt-24 text-center"
              >
                <BaseText
                  type="label"
                  size="md"
                >
                  No results found
                </BaseText>
              </div>
              <div v-if="(filters.brandProfiles || filters.status) && getBriefs.length === 0 && !loading">
                <BaseText
                  size="md"
                  type="label"
                  class="text-center my-24"
                >
                  No Results
                </BaseText>
              </div>
              <!-- Empty State -->
              <div
                v-else-if="!getBriefs.length && getInitialLoad && searchText.length == 0 && !loading"
                class="bg-white"
              >
                <div
                  class="flex flex-col items-center justify-center py-5"
                >
                  <div
                    class=" relative mb-5 mt-2"
                  >
                    <img
                      src="../../assets/icons/briefs.png"
                      alt=""
                      class="w-14 h-14"
                    >
                  </div>
                  <BaseText
                    type="label"
                    size="md"
                    class="text-text-loud text-center"
                  >
                    Get Started With Briefs
                  </BaseText>
                  <BaseText
                    type="body"
                    size="sm"
                    class="text-text-muted text-center"
                  >
                    Transform Your Inspiration Into Actionable Ad Briefs
                  </BaseText>
                  <iframe
                    class=" mt-5 max-w-md rounded-lg"
                    width="100%"
                    height="255"
                    src="https://www.youtube.com/embed/gUAspEVLZ4M?si=UqodsQ343WpyCsar"
                    title="YouTube video player"
                    frameborder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowfullscreen
                  />

                  <BaseButton
                    class="mt-5"
                    cta
                    @click="createNewBrief"
                  >
                    <div
                      class="flex items-center justify-center"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        class="text-white mr-1"
                      >
                        <path
                          d="M7.29134 5.62484H12.708M7.29134 8.95817H12.708M7.29134 12.2915H9.37467M5.62467 17.7082H14.3747C15.2951 17.7082 16.0413 16.962 16.0413 16.0415V3.95817C16.0413 3.0377 15.2951 2.2915 14.3747 2.2915H5.62467C4.7042 2.2915 3.95801 3.0377 3.95801 3.95817V16.0415C3.95801 16.962 4.7042 17.7082 5.62467 17.7082Z"
                          stroke="currentColor"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                      Create Brief
                    </div>
                  </BaseButton>
                </div>
              </div>

              <!-- Archived Table -->
              <transition
                name="fade"
                enter-active-class="transition-opacity duration-500"
                leave-active-class="transition-opacity duration-500"
                enter-class="opacity-0"
                enter-to-class="opacity-100"
                leave-class="opacity-100"
                leave-to-class="opacity-0"
              >
                <div
                  v-if="archivedBriefs.length && getInitialLoad"
                  class="mt-10"
                >
                  <div v-if="archivedBriefs.length">
                    <div>
                      <!-- HEADERS -->
                      <div
                        class="flex relative border-b border-neutral-50 bg-background-normal py-3 cursor-pointer rounded-md select-none"
                        @click="toggleShowArchived"
                      >
                        <!-- Bulk Checkbox -->
                        <div class="flex items-center justify-end px-1.5">
                          <!-- Checkbox -->
                          <input
                            type="checkbox"
                            class="h-4 w-4 ml-2 mr-3 rounded border-border-normal text-primary-green-100 focus:ring-0"
                            :checked="
                              selectedArchivedBriefs.length ===
                                archivedBriefs.length
                            "
                            @click.stop="selectAllArchivedBriefs($event)"
                          >
                        </div>

                        <div class="grid grid-cols-5 w-full">
                          <!-- Name -->
                          <div>
                            <BaseText
                              type="body"
                              size="sm"
                              class="text-neutral-400"
                            >
                              Archived
                            </BaseText>
                          </div>

                          <!-- Blank for settings -->
                          <div class="absolute right-3.5">
                            <!--  -->
                            <img
                              class="transition-all duration-300 transform"
                              :class="showArchived ? 'rotate-180' : ''"
                              src="../../assets/icons/chevron-top-small.svg"
                            >
                          </div>
                        </div>
                      </div>

                      <!-- ROWS -->
                      <transition
                        name="fade"
                        enter-active-class="transition-opacity duration-500"
                        leave-active-class="transition-opacity duration-500"
                        enter-class="opacity-0"
                        enter-to-class="opacity-100"
                        leave-class="opacity-100"
                        leave-to-class="opacity-0"
                      >
                        <div v-if="showArchived">
                          <div
                            v-for="(brief, index) of archivedBriefs"
                            :key="index"
                            class="flex items-center py-5 border-b border-neutral-50 cursor-pointer hover:bg-background-disabled transition"
                            :class="{
                              'bg-background-disabled':
                                selectedArchivedBriefs.includes(brief.id),
                            }"
                            @click="goToBrief(brief.id)"
                          >
                            <!-- Checkbox -->
                            <div class="flex items-center justify-end px-1.5">
                              <!-- Checkbox -->
                              <input
                                type="checkbox"
                                class="h-4 w-4 ml-2 mr-3 rounded border-border-normal text-primary-green-100 focus:ring-0 cursor-pointer"
                                :checked="
                                  selectedArchivedBriefs.includes(brief.id)
                                "
                                @click.stop="
                                  toggleSelectedArchiveBrief(brief.id)
                                "
                              >
                            </div>

                            <div class="grid grid-cols-5 w-full">
                              <!-- Name -->
                              <div>
                                <BaseText
                                  type="label"
                                  size="sm"
                                  class="text-neutral-700 font-medium"
                                >
                                  {{ brief.name }}
                                </BaseText>
                              </div>
                              <!-- Brand -->
                              <div class="flex items-center gap-2">
                                <img
                                  v-if="brief.brandProfileId"
                                  class="w-6 h-6 rounded-full"
                                  :src="
                                    brandProfiles?.find(
                                      (bp) => bp.id === brief?.brandProfileId
                                    )?.image
                                  "
                                >

                                <BaseText
                                  v-if="brief.brandProfileId"
                                  type="body"
                                  size="sm"
                                  class="font-normal text-neutral-700"
                                >
                                  {{
                                    brandProfiles?.find(
                                      (bp) => bp.id === brief?.brandProfileId
                                    )?.name
                                  }}
                                </BaseText>
                                <BaseText
                                  v-else
                                  type="body"
                                  size="sm"
                                  class="text-neutral-400 font-normal"
                                >
                                  {{ "No Brand Selected" }}
                                </BaseText>
                              </div>

                              <!-- Folder -->
                              <div class="flex items-center gap-2">
                                <!--  -->
                                <img src="../../assets/icons/folder.svg">

                                <BaseText
                                  type="body"
                                  size="sm"
                                  class="text-neutral-700 font-normal"
                                >
                                  {{ getBriefFolder(brief.id).name }}
                                </BaseText>
                              </div>
                              <!-- Status -->
                              <div class="flex items-center">
                                <!--  -->
                                <BaseBadge
                                  slot="reference"
                                  :blue="brief.status === 'In Progress'"
                                  :green="brief.status === 'Completed'"
                                  :yellow="brief.status === 'Needs Review'"
                                  :gray="
                                    brief.status === 'To Do' || !brief.status
                                  "
                                  dot
                                >
                                  <BaseText
                                    type="label"
                                    size="xs"
                                  >
                                    {{ brief.status || "No Status" }}
                                  </BaseText>
                                </BaseBadge>
                              </div>
                              <!-- Due Date -->
                              <div>
                                <BaseText
                                  type="body"
                                  size="sm"
                                  class="text-neutral-400 font-normal"
                                >
                                  {{
                                    brief.dueDate
                                      ? formatDate(brief.dueDate)
                                      : "No Date Set"
                                  }}
                                </BaseText>
                              </div>
                            </div>
                            <!-- 3 Dot Options -->
                            <div class="pr-4">
                              <BaseSelect
                                :items="[
                                  { name: 'Delete', action: deleteBrief },
                                  { name: 'Unarchive', action: unarchiveBrief },
                                ]"
                                text-trigger
                                no-padding
                                popper-center
                                compact
                                item-value="name"
                                @click.native.stop
                                @select="() => {}"
                              >
                                <!-- Three Dots -->
                                <img
                                  slot="reference"
                                  class="hover:bg-white border border-transparent hover:border-border-normal rounded-md transition"
                                  src="../../assets/icons/dots.svg"
                                >

                                <!-- Dropdown Items -->
                                <template #item="{ item }">
                                  <div
                                    class="flex max-w-max"
                                    style="width: max-content"
                                    @click="item.action(brief.id)"
                                  >
                                    <img
                                      v-if="item.name === 'Delete'"
                                      class="pr-2"
                                      src="../../assets/icons/trash.svg"
                                    >
                                    <img
                                      v-if="item.name === 'Unarchive'"
                                      class="pr-2"
                                      src="../../assets/icons/unarchive.svg"
                                    >
                                    <BaseText
                                      type="body"
                                      size="sm"
                                    >
                                      {{ item.name }}
                                    </BaseText>
                                  </div>
                                </template>
                              </BaseSelect>
                            </div>
                          </div>
                        </div>
                      </transition>
                    </div>
                  </div>
                </div>
              </transition>
            </div>
          </div>
        </transition>
        <!-- Confirmation Modal -->
        <ConfirmDeleteModal
          v-if="showConfirmDeleteModal"
          @confirm="deleteSelected"
          @close="showConfirmDeleteModal = false"
        />

        <!-- Bulk Actions Toolbar -->
        <div
          v-if="
            (selectedBriefs.length || selectedArchivedBriefs.length) &&
              viewToggleValue === 'Briefs'
          "
          class="fixed bottom-nav px-4 py-3 rounded-xl flex items-center appear-animation"
        >
          <!-- Selected Number -->
          <div class="flex gap-2 text-text-muted select-none mr-4">
            <div
              class="rounded-full bg-background-normal w-5 h-5 flex items-center justify-center"
            >
              <BaseText
                type="label"
                size="sm"
              >
                {{
                  selectedBriefs.length
                    ? selectedBriefs.length
                    : selectedArchivedBriefs.length
                }}
              </BaseText>
            </div>

            <BaseText
              type="body"
              size="sm"
            >
              selected
            </BaseText>
          </div>

          <!-- Divider -->
          <div class="h-6 border-r border-border-normal mx-3" />

          <!-- Archive -->
          <BaseButton
            v-if="selectedBriefs.length"
            text
            @click="archiveSelectedBriefs"
          >
            <div class="flex items-center gap-1.5">
              <img src="../../assets/icons/archive.svg">
              <BaseText
                type="label"
                size="sm"
                class="text-text-muted"
              >
                Archive
              </BaseText>
            </div>
          </BaseButton>
          <BaseButton
            v-else
            text
            @click="unarchiveSelectedBriefs"
          >
            <div class="flex items-center gap-1.5">
              <img src="../../assets/icons/unarchive.svg">
              <BaseText
                type="label"
                size="sm"
                class="text-text-muted"
              >
                Unarchive
              </BaseText>
            </div>
          </BaseButton>

          <!-- Divider -->
          <div class="h-6 border-r border-border-normal mx-3" />

          <!-- Delete -->
          <BaseButton
            text
            @click="showConfirmDeleteModal = true"
          >
            <div class="flex items-center gap-1.5">
              <img src="../../assets/icons/trash.svg">
              <BaseText
                type="label"
                size="sm"
                class="text-secondary-red-100"
              >
                Delete
              </BaseText>
            </div>
          </BaseButton>
        </div>

        <!-- Brand Profile View -->
        <div class="w-full justify-center">
          <transition
            name="fade"
            enter-active-class="transition-opacity duration-700"
            leave-active-class="transition-opacity duration-500"
            enter-class="opacity-0"
            enter-to-class="opacity-100"
            leave-class="opacity-100"
            leave-to-class="opacity-0"
          >
            <div
              v-if="viewToggleValue === 'Brand Profiles' && !transitioning"
              class="p-6 text-center"
              style="max-width: 1200px"
            >
              <BaseModal
                v-if="selectedBrandProfile"
                :card-width="'650px'"
                @close="changeProfile(null)"
              >
                <div
                  style="max-height: calc(90vh - 200px)"
                  class="px-4 overflow-y-scroll h-full"
                >
                  <BrandProfileDetails
                    small-card
                    :always-show-more="true"
                    :brand-profile="selectedBrandProfile"
                    @changeProfile="changeProfile"
                  />
                </div>
              </BaseModal>

              <BrandProfileGrid
                v-if="!selectedBrandProfile"
                :brand-profiles="brandProfiles"
                brand-profiles-page
                @createNewBrand="createNewBrand"
                @selectBrandProfile="changeProfile($event)"
              />
            </div>
          </transition>
        </div>
        <CreateBriefModal
          v-if="showCreateBriefModal"
          :initial-folder="modalInitialFolder"
          @close="showCreateBriefModal = false"
        />

        <TutorialModal
          v-if="showTutorialModal"
          @close="showTutorialModal = false"
        />
      </div>
    </template>
  </MainLayout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import MainLayout from '../../layouts/MainLayout'
import ConfirmDeleteModal from '../../components/globals/ConfirmDeleteModal.vue'
import FirebaseAPI from '@/api/firebase'
import posthog from 'posthog-js'
import firebase from '@/api/config/FirebaseConfig'
import moment from 'moment'
import _ from 'lodash'
import TutorialModal from '../../components/advertisements/TutorialModal.vue'
import BrandProfileGrid from '../../components/briefsV2/brandProfile/BrandProfileGrid.vue'
import BrandProfileDetails from '../../components/briefsV2/brandProfile/BrandProfileDetails.vue'
import CreateBriefModal from '../../components/briefsV2/CreateBriefModal.vue'
import PlusIcon from '../../components/globals/Icons/PlusIcon.vue'

// Icons
import BriefsTabIcon from '../../components/globals/Icons/BriefsIcons/BriefsTabIcon.vue'
import BrandProfilesIcon from '../../components/globals/Icons/BriefsIcons/BrandProfilesIcon.vue'

export default {
  name: 'BriefsView',
  components: {
    ConfirmDeleteModal,
    MainLayout,
    BrandProfileGrid,
    BrandProfileDetails,
    CreateBriefModal,
    TutorialModal,
    PlusIcon,
    BriefsTabIcon,
    BrandProfilesIcon
  },
  data () {
    return {
      selectedBrandProfile: null,
      selectedFolders: [],
      selectedStatus: [],
      filtersApplied: false,
      showArchived: false,
      docOrder: 'desc',
      sortOrder: [{ name: 'Newest', value: 'desc' }],
      viewToggleValue: 'Briefs',
      brandProfiles: [],
      brandProfilesLoading: true,
      textSearch: '',
      showCreateBriefModal: false,
      modalInitialFolder: { name: 'Default Folder', id: 'default' },
      showTutorialModal: false,
      archivedBriefs: [],
      // For Bulk Actions
      selectedBriefs: [],
      selectedArchivedBriefs: [],
      showConfirmDeleteModal: false,
      transitioning: false,
      filteredBriefs: [],
      showAllBriefs: true,
      clearFilters: false,
      loading: false,

      infiniteId: +new Date(),
      searchText: '',
      loadingMore: false,

      filters: {
        status: null,
        brandProfiles: null
      },
      // Filter dropdowns
      selectedBrandProfileFilter: { label: 'All Brand Profiles', value: null },
      selectedStatusFilter: { label: 'All Statuses', value: null }
    }
  },
  computed: {
    ...mapGetters('AdvertisementsModule', ['getAdsBeingSaved']),
    ...mapGetters('AuthModule', [
      'getUser',
      'getTeam',
      'isFreeTier',
      'getUserFreeTrialDate'
    ]),
    ...mapGetters('AdvertisementsModule', ['getAdsBeingSaved']),
    ...mapGetters('BriefsModule', ['getBriefs', 'getInitialLoad', 'getLastBriefDoc']),
    ...mapGetters('BoardsModule', ['getBoards', 'getFolders']),
    ...mapGetters('TagsModule', ['getTags']),
    briefs () {
      return this.getBriefs
      // let duplicateArr = [...this.getBriefs]

      // // Filter for folder
      // if (this.selectedFolders.length) {
      //   // Find the folder to filter for
      //   const folder = this.getFolders.find(
      //     (folder) => folder.id === this.selectedFolders[0].id
      //   )

      //   // Get all briefs for that folder
      //   duplicateArr = folder.briefIds
      //     ? folder.briefIds
      //       .map((briefId) =>
      //         this.getBriefs.find((brief) => brief.id === briefId)
      //       )
      //       .filter((brief) => brief)
      //     : []
      // }

      // // Filter for status
      // if (this.selectedStatus.length) {
      //   duplicateArr = duplicateArr.filter((brief) => {
      //     if (this.selectedStatus[0].name === 'No Status') {
      //       return (
      //         !brief.status || brief.status === this.selectedStatus[0].name
      //       )
      //     }

      //     return brief.status === this.selectedStatus[0].name
      //   })
      // }

      // if (this.sortOrder[0].value === 'asc') {
      //   return duplicateArr.sort((a, b) =>
      //     a.createdAt <= b.createdAt ? 1 : -1
      //   )
      // }

      // return duplicateArr.sort((a, b) => (a.createdAt >= b.createdAt ? 1 : -1))
    }
  },
  watch: {
    filteredBriefs () {
      this.showAllBriefs = false
    }
  },
  async mounted () {
    this.loading = true
    await this.fetchBriefs()
    await this.fetchBrandProfiles()
    this.loading = false

    await this.getArchivedBriefs()

    if (this.$route.query.folderId) {
      const folderId = this.$route.query.folderId
      if (folderId !== 'default') this.modalInitialFolder = this.getFolders.find(folder => folder.id === folderId)
      this.showCreateBriefModal = true
      history.replaceState({}, null, '/briefs')
    }
  },
  methods: {
    ...mapActions('BriefsModule', ['fetchBriefs', 'fetchMoreBriefs', 'fetchBriefByText']),
    ...mapActions('BoardsModule', ['fetchFolders']),
    async handleSort () {
      const payload = {
        search: this.searchText,
        brandProfile: this.filters.brandProfiles,
        status: this.filters.status,
        sort: this.sortOrder?.[0].value
      }

      this.loading = true
      try {
        await this.fetchBriefByText(payload)
      } finally {
        this.loading = false
      }
    },
    async handleFilterUpdate (checkedItems) {
      if (checkedItems.length === 0) {
        this.filters = {
          brandProfiles: null,
          status: null
        }
      }
      const statusOptions = ['Completed', 'Needs Review', 'In Progress', 'To Do', 'No Status']
      checkedItems.forEach(item => {
        if (statusOptions.includes(item)) {
          this.filters.status = item
        } else {
          this.filters.brandProfiles = item
        }
      })
      const payload = {
        search: this.searchText,
        brandProfile: this.filters.brandProfiles,
        status: this.filters.status,
        sort: this.sortOrder?.[0].value
      }
      this.loading = true
      await this.fetchBriefByText(payload)
      this.loading = false
    },
    async handleBriefSearch (event) {
      this.searchText = event
      await this.searchBrief(event)
    },
    searchBrief: _.debounce(async function (event) {
      const payload = {
        search: event,
        brandProfile: this.filters.brandProfiles,
        status: this.filters.status,
        sort: this.sortOrder.value
      }
      this.loading = true
      await this.fetchBriefByText(payload)
      this.loading = false
    }, 300),
    async getMoreBriefs () {
      this.loadingMore = true
      if (this.trialExpired || !this.getInitialLoad) {
        return
      }

      console.log('%c FETCHING MORE BRIEFS', 'font-size: 14px;')
      try {
        const query = {
          search: this.searchText,
          brandProfile: this.filters.brandProfiles,
          status: this.filters.status,
          sort: this.sortOrder.value
        }
        await this.fetchMoreBriefs(query)
      } catch (e) {
        this.$showAlert({ type: 'error', message: e.message })
        this.loadingMore = false
      }
      this.loadingMore = false
    },
    async duplicateBrief (briefId) {
      const briefToDuplicate = await FirebaseAPI.Briefs.getByID(briefId)
      // Create a new empty brief
      const briefPayload = {
        ...briefToDuplicate,
        name: `${briefToDuplicate.name} - Copy`,
        createdBy: this.getUser?.user_id || this.getUser?.id,
        createdAt: new Date().getTime(),
        modifiedAt: new Date().getTime()
      }

      delete briefPayload.id
      delete briefPayload.submissions
      delete briefPayload.inElasticSearch

      // Check for team
      if (this.getTeam) {
        briefPayload.teamId = this.getTeam.id
      }

      const brief = await FirebaseAPI.Briefs.create(briefPayload)

      // Update sidebar
      await this.fetchBriefs()

      // Send the user to the edit page for the new brief
      this.$router.push({ name: 'EditBriefView', params: { id: brief.id } })

      this.loading = false
    },
    async shareBrief (briefId) {
      await navigator.clipboard.writeText(
        process.env.VUE_APP_BASE_URL + '/share/briefs/' + briefId
      )

      this.$showAlert({
        message: 'Copied share link to clipboard!',
        type: 'success'
      })
    },
    transitionTimeout () {
      setTimeout(() => {
        this.transitioning = false
      }, 400)
    },
    async changeProfile (brandProfile) {
      this.selectedBrandProfile = brandProfile

      this.fetchBrandProfiles()
    },
    async createNewBrand () {
      const user = firebase.auth().currentUser

      const newBrandPayload = {
        name: 'Brand Name',
        createdBy: user.id,
        createdAt: +new Date(),
        modifiedAt: +new Date()
      }

      if (this.getTeam) {
        newBrandPayload.teamId = this.getTeam.id
      }

      // Create one in firestore
      const brandProfileInstance = await FirebaseAPI.BrandProfiles.create(
        newBrandPayload
      )
      newBrandPayload.id = brandProfileInstance.id

      // Create a new brand
      this.selectedBrandProfile = newBrandPayload
    },
    async fetchBrandProfiles () {
      this.brandProfilesLoading = true
      try {
        if (this.getTeam) {
          const brandProfiles = await FirebaseAPI.BrandProfiles.getAllByTeam(
            this.getTeam.id
          )
          this.brandProfiles = brandProfiles.sort((a, b) => a.name.localeCompare(b.name.trim()))
        } else {
          const brandProfiles = await FirebaseAPI.BrandProfiles.getAllByUser()
          this.brandProfiles = brandProfiles.sort((a, b) => a.name.localeCompare(b.name.trim()))
        }
      } catch (error) {
        console.error('Error fetching brand profiles:', error)
      } finally {
        this.brandProfilesLoading = false
      }
    },
    selectAllBriefs (e) {
      if (!e.target.checked) {
        this.selectedBriefs = []
        return
      }

      this.selectedArchivedBriefs = []
      this.selectedBriefs = this.briefs.map((brief) => brief.id)
    },
    selectAllArchivedBriefs (e) {
      if (!e.target.checked) {
        this.selectedArchivedBriefs = []
        return
      }

      this.selectedBriefs = []
      this.showArchived = true
      this.selectedArchivedBriefs = this.archivedBriefs.map(
        (brief) => brief.id
      )
    },
    toggleSelectedArchiveBrief (briefId) {
      if (this.selectedBriefs.length) {
        this.selectedBriefs = []
      }

      if (this.selectedArchivedBriefs.includes(briefId)) {
        this.selectedArchivedBriefs = this.selectedArchivedBriefs.filter(
          (id) => id !== briefId
        )
        return
      }

      this.selectedArchivedBriefs = this.selectedArchivedBriefs.concat(briefId)
    },
    toggleSelectedBrief (briefId) {
      if (this.selectedArchivedBriefs.length) {
        this.selectedArchivedBriefs = []
      }

      if (this.selectedBriefs.includes(briefId)) {
        this.selectedBriefs = this.selectedBriefs.filter(
          (id) => id !== briefId
        )
        return
      }

      this.selectedBriefs = this.selectedBriefs.concat(briefId)
    },
    getFormattedTimes () {
      const datesMap = {}
      const formattedTimes = []

      this.getBriefs.forEach((brief) => {
        const dueDate = brief.dueDate
        const formattedDate = this.formatDueDate(dueDate)

        if (!datesMap[formattedDate]) {
          datesMap[formattedDate] = 1
          formattedTimes.push({
            subItem: formattedDate,
            timestamp: [dueDate],
            briefIds: [brief.id],
            count: 1,
            icon: 'due date',
            hover: true,
            id: brief.id
          })
        } else {
          datesMap[formattedDate]++
          const index = formattedTimes.findIndex(
            (item) => item.subItem === formattedDate
          )
          if (index !== -1) {
            formattedTimes[index].briefIds.push(brief.id)
            formattedTimes[index].timestamp.push(dueDate)
            formattedTimes[index].count = datesMap[formattedDate]
          }
        }
      })

      return formattedTimes
    },
    formatDueDates (timestamps) {
      if (!Array.isArray(timestamps)) {
        return []
      }

      const formattedDates = []
      const timestampCount = {}

      for (let i = 0; i < timestamps.length; i++) {
        const timestamp = timestamps[i]
        const count = timestampCount[timestamp] || 0
        timestampCount[timestamp] = count + 1

        const formattedDate = this.formatDueDate(timestamp, count)
        formattedDates.push(formattedDate)
      }

      return formattedDates
    },
    formatDueDate (timestamp) {
      if (!timestamp) {
        return 'No due date'
      }

      const now = new Date()
      const dueDate = new Date(timestamp)
      const timeDiff = dueDate.getTime() - now.getTime()

      const oneDay = 24 * 60 * 60 * 1000
      const oneMonth = 30 * oneDay
      const oneYear = 365 * oneDay

      if (timeDiff < oneDay) {
        const days = Math.abs(Math.ceil(timeDiff / (1000 * 60 * 60 * 24)))
        return `${days} ${days === 1 ? 'day' : 'days'} ${
          timeDiff < 0 ? 'ago' : 'from now'
        }`
      } else if (timeDiff < oneMonth) {
        const months = Math.abs(Math.floor(timeDiff / oneMonth))
        return `${months} ${months === 1 ? 'month' : 'months'} ${
          timeDiff < 0 ? 'ago' : 'from now'
        }`
      } else if (timeDiff < oneYear) {
        const years = Math.abs(Math.floor(timeDiff / oneYear))
        return `${years} ${years === 1 ? 'year' : 'years'} ${
          timeDiff < 0 ? 'ago' : 'from now'
        }`
      } else {
        return 'More than a year from now'
      }
    },
    async deleteSelected () {
      if (!this.selectedBriefs.length) return
      await Promise.all(this.selectedBriefs.map((briefId) => this.deleteBrief(briefId, false)))

      this.selectedBriefs = []
      this.selectedArchivedBriefs = []

      await this.fetchBriefs()
      await this.fetchFolders()

      this.showConfirmDeleteModal = false
      this.$showAlert({
        message: 'Deleted Selected Briefs',
        type: 'success'
      })
    },
    async deleteBrief (briefId, refresh = true) {
      await FirebaseAPI.Briefs.remove(briefId)

      // Remove the brief from its folder (if not default)
      const folder = this.getBriefFolder(briefId)
      if (folder.id !== 'default') {
        const toRemovePayload = {
          briefIds: folder.briefIds.filter((id) => id !== briefId)
        }
        await FirebaseAPI.Folders.update(folder.id, toRemovePayload)
      }

      if (refresh) {
        setTimeout(async () => {
          await this.getArchivedBriefs()
          await this.fetchBriefs()
          await this.fetchFolders()
          this.$showAlert({
            message: 'Deleted Brief',
            type: 'success'
          })
        }, 1000)
      }
    },
    async archiveSelectedBriefs () {
      if (!this.selectedBriefs.length) return

      await Promise.all(
        this.selectedBriefs.map((briefId) => {
          return FirebaseAPI.Briefs.update(briefId, { archived: true })
        })
      )

      this.selectedBriefs = []

      await this.fetchBriefs()
      await this.getArchivedBriefs()

      this.$showAlert({
        message: 'Archived Selected Briefs',
        type: 'success'
      })
    },
    async unarchiveSelectedBriefs () {
      if (!this.selectedArchivedBriefs.length) return

      await Promise.all(
        this.selectedArchivedBriefs.map((briefId) => {
          return FirebaseAPI.Briefs.update(briefId, { archived: true })
        })
      )

      this.selectedArchivedBriefs = []

      await this.fetchBriefs()
      await this.getArchivedBriefs()

      this.$showAlert({
        message: 'Archived Selected Briefs',
        type: 'success'
      })
    },
    async unarchiveBrief (briefId) {
      await FirebaseAPI.Briefs.update(briefId, { archived: false })
      await this.getArchivedBriefs()
      await this.fetchBriefs()

      this.$showAlert({
        message: 'Unarchived Brief',
        type: 'success'
      })
    },
    async archiveBrief (briefId) {
      await FirebaseAPI.Briefs.update(briefId, { archived: true })
      await this.getArchivedBriefs()
      await this.fetchBriefs()

      this.$showAlert({
        message: 'Archived Brief',
        type: 'success'
      })
    },
    async toggleShowArchived () {
      if (this.showArchived) {
        this.showArchived = false
        return
      }

      this.showArchived = true

      await this.getArchivedBriefs()
    },
    async getArchivedBriefs () {
      this.archivedBriefs = await FirebaseAPI.Briefs.getAllArchived(
        this.getTeam?.id
      )
    },
    getBriefFolder (briefId) {
      const briefFolder = this.getFolders.find((folder) =>
        folder.briefIds?.includes(briefId)
      )
      return briefFolder || { id: 'default', name: 'Default Folder' }
    },
    formatDate (timeStamp) {
      return moment(timeStamp).format('MMM Do')
    },
    goToBrief (briefId) {
      this.$router.push({ name: 'EditBriefView', params: { id: briefId } })
    },
    async createNewBrief () {
      // Create a new empty brief
      const briefPayload = {
        name: 'Untitled Brief',
        createdAt: new Date().getTime(),
        components: [
          {
            type: 'description',
            value: ''
          }
        ]
      }

      // Check for team
      if (this.getTeam) {
        briefPayload.teamId = this.getTeam.id
      }

      const brief = await FirebaseAPI.Briefs.create(briefPayload)

      // PostHog tracking
      posthog.capture('Created Brief', {
        brief_id: brief.id,
        brief_name: briefPayload.name
      })

      // Update sidebar
      await this.fetchBriefs()

      // Send the user to the edit page for the new brief
      this.$router.push({ name: 'EditBriefView', params: { id: brief.id } })
    },
    toggleOrder () {
      if (this.docOrder === 'asc') {
        this.docOrder = 'desc'
      } else {
        this.docOrder = 'asc'
      }

      // Fetch briefs
    },
    handleBrandProfileFilterChange (selectedBrandProfile) {
      this.selectedBrandProfileFilter = selectedBrandProfile
      this.filters.brandProfiles = selectedBrandProfile && selectedBrandProfile.value !== null
        ? selectedBrandProfile.value
        : null
      const payload = {
        search: this.searchText,
        brandProfile: this.filters.brandProfiles,
        status: this.filters.status,
        sort: this.sortOrder[0].value
      }
      this.loading = true
      this.fetchBriefByText(payload).then(() => {
        this.loading = false
      })
    },
    handleStatusFilterChange (selectedStatus) {
      this.selectedStatusFilter = selectedStatus
      this.filters.status = selectedStatus && selectedStatus.value !== null
        ? selectedStatus.value
        : null
      const payload = {
        search: this.searchText,
        brandProfile: this.filters.brandProfiles,
        status: this.filters.status,
        sort: this.sortOrder[0].value
      }
      this.loading = true
      this.fetchBriefByText(payload).then(() => {
        this.loading = false
      })
    }
  },
  metaInfo: {
    title: 'Foreplay.co | Briefs'
  }
}
</script>

<style scoped>
.bottom-nav {
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0px 8px 24px -12px rgba(2, 23, 59, 0.36),
    0px 1px 2px rgba(164, 172, 185, 0.16),
    0px 0px 0px 1px rgba(18, 55, 105, 0.08);
  backdrop-filter: blur(5px);

  z-index: 57;
  transform: translateX(-50%);

  /* 138 is half of the sidebar width */
  left: calc(50% + 138px);
  bottom: 38px;

  border-radius: 12px;
}

.appear-animation {
  animation: appear 300ms ease-in;
  animation-iteration-count: 1;
}

@keyframes appear {
  0% {
    opacity: 0;
    transform: translate(-50%, 20px);
  }
  100% {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

/* Library tab styles */
.library-view-tab {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.625rem;
  border-radius: 0.375rem;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: #5E6678; /* text-text-normal */
  background-color: transparent;
  cursor: pointer;
  user-select: none; /* Standard */
  transition: color 150ms ease-in-out, background-color 150ms ease-in-out;
  white-space: nowrap;
}
.library-view-tab:hover {
  color: #303546; /* text-text-muted */
  background-color: #F6F8FA; /* bg-neutral-25 */
}
.library-view-tab.active {
  color: #303546; /* text-text-muted */
  background-color: #F6F8FA; /* bg-neutral-25 */
  font-weight: 500;
}

/* Dropdown skeleton styles */
.dropdown-parent-skeleton {
  position: absolute;
  left: 0;
  top: 0;
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.08), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  border-radius: 8px;
  background-color: white;
  width: 100%;
}
</style>
